import { useMemo, useState, useEffect } from "react";
import { Box, Typography, Stack, Button, ToggleButtonGroup, ToggleButton, CircularProgress, Skeleton, Alert } from "@mui/material";
import dayjs from "dayjs";
import { useResponsive } from "../../../utils/mobileresponsive";
import { isFieldAgent } from "../../../utils/roleDetection";
import { LevelColumn } from "../components/OverrideLevel";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import HelpIcon from "@mui/icons-material/Help";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import LevelRatesDialog from "../components/LevelRatesDialog";
import OverrideQualificationDialog from "../components/OverrideQualiDialog";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import { MonthPicker } from "../../../components/ui/MonthPicker";

import useFetchAgentHierarchy from "../hooks/useFetchAgentDownlines";

// ─── Helper: get current period based on 15th cutoff (matches backend logic) ──

const getCurrentPeriod = () => {
	const now = new Date();
	const day = now.getDate();
	let year = now.getFullYear();
	let month = now.getMonth() + 1;

	if (day > 15) {
		month++;
		if (month > 12) {
			month = 1;
			year++;
		}
	}

	return `${year}-${String(month).padStart(2, "0")}`;
};

const getStatusStyle = (status) => {
	if (status?.toLowerCase() === "paid") {
		return {
			bg: "rgba(34,197,94,0.1)",
			border: "rgba(34,197,94,0.3)",
			color: "#16a34a",
		};
	}
	// Pending
	return {
		bg: "rgba(234,179,8,0.1)",
		border: "rgba(234,179,8,0.3)",
		color: "#ca8a04",
	};
};

// ─── Helper functions ──────────────────────────────────────────

const buildLevels = (hierarchyData) => {
	if (!hierarchyData) return { l1: [], l2: [], l3: [] };

	const agentNameMap = new Map();
	agentNameMap.set(Number(hierarchyData.agent_id), hierarchyData.agent_name);
	[...(hierarchyData.l1 ?? []), ...(hierarchyData.l2 ?? []), ...(hierarchyData.l3 ?? [])].forEach((a) => {
		agentNameMap.set(a.ID, a.name);
	});

	const toCard = (agent) => ({
		ID: agent.ID,
		name: agent.name,
		source_agent: agent.source_agent ?? null,
		source_agent_name: agentNameMap.get(agent.source_agent) ?? "",
		members: agent.network_members || 0,
		application: agent.active_members || 0,
		override: `$${(agent.override_earning || 0).toFixed(2)}`,
		// Opted-out agents are no longer returned by name. What is left is an
		// anonymised placeholder, kept only to anchor its opted-in downlines.
		isPlaceholder: agent.is_placeholder === true,
	});

	return {
		l1: hierarchyData.l1?.map(toCard) ?? [],
		l2: hierarchyData.l2?.map(toCard) ?? [],
		l3: hierarchyData.l3?.map(toCard) ?? [],
	};
};

const buildAgentMap = (data) => {
	const map = new Map();
	Object.values(data).forEach((level) => {
		level.forEach((item) => map.set(item.ID, item.name));
	});
	return map;
};

const getChain = (id, data, parentMap) => {
	const all = [...data.l1, ...data.l2, ...data.l3];

	const childrenMap = new Map();
	all.forEach((item) => {
		if (!childrenMap.has(item.source_agent)) childrenMap.set(item.source_agent, []);
		childrenMap.get(item.source_agent).push(item.ID);
	});

	const result = new Set();

	let current = id;
	while (current) {
		result.add(current);
		current = parentMap.get(current);
	}

	const queue = [id];
	while (queue.length) {
		const node = queue.shift();
		(childrenMap.get(node) || []).forEach((child) => {
			if (!result.has(child)) {
				result.add(child);
				queue.push(child);
			}
		});
	}

	return result;
};

const OverrideSummary = ({ thisMonth = 0, lastMonth = 0, payoutStatus = "Pending", loading = false }) => {
	const change = thisMonth - lastMonth;
	const changePct = lastMonth > 0 ? ((change / lastMonth) * 100).toFixed(1) : thisMonth > 0 ? 100 : 0;
	const isPositive = change >= 0;
	const statusStyle = getStatusStyle(payoutStatus);

	const StatChip = ({ label, value, accent }) => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: { xs: 80, sm: 96 } }}>
			<Typography
				sx={{
					fontSize: { xs: 9, sm: 10 },
					fontWeight: 600,
					color: "#9ca3af",
					textTransform: "uppercase",
					letterSpacing: "0.07em",
					whiteSpace: "nowrap",
				}}
			>
				{label}
			</Typography>
			{loading ? (
				<Skeleton variant="text" width={64} height={28} />
			) : (
				<Typography
					sx={{
						fontSize: { xs: 16, sm: 20 },
						fontWeight: 800,
						color: accent ? "#1D54A2" : "#111827",
						lineHeight: 1.15,
						whiteSpace: "nowrap",
					}}
				>
					{value}
				</Typography>
			)}
		</Box>
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 } }}>
			<Typography
				sx={{
					fontSize: { xs: 11, sm: 13 },
					fontWeight: 800,
					color: "#1D54A2",
					letterSpacing: "0.12em",
					textTransform: "uppercase",
					whiteSpace: "nowrap",
				}}
			>
				Summary
			</Typography>

			<Box sx={{ width: "1.5px", height: { xs: 32, sm: 40 }, background: "#d1d5db", flexShrink: 0 }} />

			<StatChip label="This Month" value={`$${thisMonth?.toFixed(2) || "0.00"}`} accent />

			<Box sx={{ width: "1px", height: { xs: 28, sm: 34 }, background: "#e5e7eb", flexShrink: 0 }} />

			<StatChip label="Last Month" value={`$${lastMonth?.toFixed(2) || "0.00"}`} />

			<Box sx={{ width: "1px", height: { xs: 28, sm: 34 }, background: "#e5e7eb", flexShrink: 0 }} />

			{loading ? (
				<Skeleton variant="rounded" width={70} height={28} sx={{ borderRadius: "20px" }} />
			) : (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "4px",
						px: { xs: "8px", sm: "18px" },
						py: { xs: "3px", sm: "4px" },
						borderRadius: "20px",
						background: isPositive ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
						border: `1px solid ${isPositive ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
					}}
				>
					{isPositive ? (
						<TrendingUpIcon sx={{ fontSize: { xs: 13, sm: 15 }, color: "#16a34a" }} />
					) : (
						<TrendingDownIcon sx={{ fontSize: { xs: 13, sm: 15 }, color: "#dc2626" }} />
					)}
					<Box>
						<Typography
							sx={{
								fontSize: { xs: 8, sm: 9 },
								fontWeight: 600,
								color: "#9ca3af",
								textTransform: "uppercase",
								letterSpacing: "0.07em",
								lineHeight: 1,
							}}
						>
							Change
						</Typography>
						<Typography
							sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 800, color: isPositive ? "#16a34a" : "#dc2626", lineHeight: 1.2 }}
						>
							{isPositive ? "+" : ""}
							{changePct}%
						</Typography>
					</Box>
				</Box>
			)}
			<Box sx={{ width: "1px", height: { xs: 28, sm: 34 }, background: "#e5e7eb", flexShrink: 0 }} />

			{loading ? (
				<Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: "20px" }} />
			) : (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						px: { xs: "8px", sm: "14px" },
						py: { xs: "4px", sm: "5px" },
						borderRadius: "20px",
						background: statusStyle.bg,
						border: `1px solid ${statusStyle.border}`,
					}}
				>
					<Typography
						sx={{
							fontSize: { xs: 8, sm: 9 },
							fontWeight: 700,
							color: "#9ca3af",
							textTransform: "uppercase",
							letterSpacing: "0.07em",
							mr: "6px",
						}}
					>
						Status
					</Typography>

					<Typography
						sx={{
							fontSize: { xs: 11, sm: 13 },
							fontWeight: 800,
							color: statusStyle.color,
							textTransform: "capitalize",
						}}
					>
						{payoutStatus}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

const HelpButtons = () => (
	<Stack direction="row" alignItems="center" justifyContent={"flex-end"} gap={1.5}>
		<LevelRatesDialog
			trigger={
				<Button
					variant="text"
					endIcon={<HelpIcon />}
					sx={{
						backgroundColor: "transparent !important",
						...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
					}}
				>
					Level Rates
				</Button>
			}
		/>
		<OverrideQualificationDialog
			trigger={
				<Button
					variant="text"
					endIcon={<HelpIcon />}
					sx={{
						backgroundColor: "transparent !important",
						...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
					}}
				>
					Override Qualification
				</Button>
			}
		/>
	</Stack>
);

// ─── Period Toggle (Month view vs All time) ────────────────────────────────

const PeriodToggle = ({ viewMode, onChangeMode, selectedMonth, onChangeMonth }) => {
	const handleToggleClick = (newValue) => {
		if (newValue === null) return;
		onChangeMode(newValue);
	};

	return (
		<Stack direction="row" alignItems="center" gap={1}>
			<ToggleButtonGroup
				value={viewMode}
				exclusive
				onChange={(e, newValue) => handleToggleClick(newValue)}
				size="small"
				sx={{
					"& .MuiToggleButton-root": {
						textTransform: "none",
						fontWeight: 600,
						fontSize: { xs: 12, sm: 13 },
						px: { xs: 1.5, sm: 2 },
						py: 0.75,
						color: "#6b7280",
						borderColor: "rgba(29,84,162,0.2)",
						backgroundColor: "transparent",
						"&.Mui-selected": {
							backgroundColor: "#1D54A2",
							color: "#fff",
							"&:hover": {
								backgroundColor: "#1a4a8f",
							},
						},
					},
				}}
			>
				<ToggleButton value="month">
					<MonthPicker value={selectedMonth} onChange={onChangeMonth} invertedColor={viewMode === "month"} />
				</ToggleButton>
				<ToggleButton value="all">
					All Time
					<AllInclusiveIcon sx={{ fontSize: { xs: 14, sm: 16 }, ml: 0.75 }} />
				</ToggleButton>
			</ToggleButtonGroup>
		</Stack>
	);
};

// ─── Full-page loader (used while we don't yet know which agent to fetch) ──

const PageLoader = () => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 2,
			py: 10,
		}}
	>
		<CircularProgress sx={{ color: "#1D54A2" }} />
		<Typography sx={{ fontSize: 14, color: "#6b7280", fontWeight: 600 }}>Loading override data…</Typography>
	</Box>
);

export default function OverridesShell({ agentId = null, headerSlot = null, footerSlot = null, agentLoading = false }) {
	const { isMobile } = useResponsive();
	const [selectedId, setSelectedId] = useState(null);
	const [viewMode, setViewMode] = useState("month");

	const [user, setUser] = useState(null);
	const [isAgent, setIsAgent] = useState(false);

	useEffect(() => {
		const stored = localStorage.getItem("loggedin_data");
		if (stored) {
			const parsedUser = JSON.parse(stored);
			setUser(parsedUser);
			const roles = Array.isArray(parsedUser?.roles) ? parsedUser.roles : [];
			setIsAgent(isFieldAgent(roles));
		}
	}, []);

	const loggedInName = user ? `${user?.name ?? ""} ${user?.last_name ?? ""}`.trim() : "";

	const [selectedMonth, setSelectedMonth] = useState(() => {
		const period = getCurrentPeriod();
		const [year, month] = period.split("-");
		return dayjs(`${year}-${month}-01`);
	});

	const activeAgentId = isAgent ? null : agentId;
	const activePeriod = viewMode === "month" ? selectedMonth.format("YYYY-MM") : null;

	const { hierarchy, loading: hierarchyLoading } = useFetchAgentHierarchy({
		agentId: activeAgentId,
		period: activePeriod,
		enabled: !agentLoading && !!activeAgentId,
	});

	const { l1, l2, l3 } = useMemo(() => {
		if (!hierarchy) return { l1: [], l2: [], l3: [] };
		return buildLevels(hierarchy);
	}, [hierarchy]);

	const levels = [
		{ key: "l1", data: l1 },
		{ key: "l2", data: l2 },
		{ key: "l3", data: l3 },
	];

	const agentMap = buildAgentMap({ l1, l2, l3 });

	const parentMap = new Map();
	[l1, l2, l3].forEach((level) => {
		level.forEach((item) => {
			if (item.source_agent) parentMap.set(item.ID, item.source_agent);
		});
	});

	const highlighted = selectedId ? getChain(selectedId, { l1, l2, l3 }, parentMap) : new Set();

	const placeholderCards = useMemo(() => [...l1, ...l2, ...l3].filter((a) => a.isPlaceholder).length, [l1, l2, l3]);

	// currentAgent now derives entirely from hierarchy response.
	const currentAgent = useMemo(() => {
		if (!hierarchy) return null;

		return {
			ID: hierarchy.agent_id,
			name: hierarchy.agent_name,
			network_members: hierarchy.network_members,
			active_members: hierarchy.active_members,
			overrides: {
				total: hierarchy.curr_total ?? 0,
				last_month_total: hierarchy.prev_total ?? 0,
				payout_status: hierarchy.payout_status,
			},
		};
	}, [hierarchy]);

	const noAgentSelected = !agentLoading && !activeAgentId;

	return (
		<Box p={4}>
			{/* ── Page title row ── */}
			<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent="space-between" spacing={2} px={3} mb={3}>
				<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
					OVERRIDE BREAKDOWN
				</Typography>
				{headerSlot}
			</Stack>

			{/* ── Agent still loading: show full-page loader, nothing else makes sense yet ── */}
			{agentLoading ? (
				<PageLoader />
			) : noAgentSelected && !isAgent ? (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
						py: 10,
						px: 3,
						background: "#fff",
						border: "1px solid rgba(29,84,162,0.1)",
						borderRadius: "12px",
					}}
				>
					<Typography sx={{ fontWeight: 700, color: "#1a2f5a", fontSize: 16 }}>No agent selected</Typography>
					<Typography sx={{ color: "#6b7280", fontSize: 13 }}>Select an agent to view their override breakdown.</Typography>
				</Box>
			) : (
				<>
					{/* ── Toolbar bar ── */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 2,
							px: 3,
							py: { xs: 1.5, sm: 2 },
							mb: 3,
							background: "#fff",
							border: "1px solid rgba(29,84,162,0.1)",
							borderRadius: "12px",
							boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
						}}
					>
						<OverrideSummary
							thisMonth={currentAgent?.overrides?.total}
							lastMonth={currentAgent?.overrides?.last_month_total}
							payoutStatus={currentAgent?.overrides?.payout_status}
							loading={hierarchyLoading}
						/>

						<Stack direction="row" alignItems="center" gap={{ xs: 1.5, sm: 2.5 }} flexWrap="wrap">
							<PeriodToggle
								viewMode={viewMode}
								onChangeMode={setViewMode}
								selectedMonth={selectedMonth}
								onChangeMonth={setSelectedMonth}
							/>
						</Stack>
					</Box>

					{/* The root can no longer be an opted-out agent — they are excluded from
					    the agent picker and cannot log in — so the banner that used to
					    explain a $0 root has gone. This one explains the unnamed cards. */}
					{placeholderCards > 0 && (
						<Alert severity="info" sx={{ mb: 3, borderRadius: 2, alignItems: "center", "& .MuiAlert-message": { py: 0.5 } }}>
							<Typography fontSize={12}>
								<strong>
									{placeholderCards} agent{placeholderCards === 1 ? "" : "s"}
								</strong>{" "}
								in this network {placeholderCards === 1 ? "has" : "have"} opted out and been removed from the portal. Their position is shown
								unnamed only so the opted-in agents beneath them keep their place, and they contribute $0 to these totals.
							</Typography>
						</Alert>
					)}

					<HelpButtons />

					{/* ── Level columns grid ── */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: "16px",
							alignItems: "start",
						}}
					>
						{levels.map(({ key, data }) => (
							<LevelColumn
								key={key}
								levelKey={key}
								agents={data}
								agentMap={agentMap}
								currentAgent={currentAgent}
								onSelect={setSelectedId}
								highlighted={highlighted}
								selectedId={selectedId}
								isAgent={isAgent}
								loggedInName={loggedInName}
								loading={hierarchyLoading}
							/>
						))}
					</Box>
				</>
			)}

			{footerSlot}
		</Box>
	);
}
